import React, { useMemo, useState } from "react";
import { Table, Button, Spin, Row, Col, Statistic, Modal, List, Tag } from "antd";
import { useGetCompanyAnalyticsQuery } from "../../services/analytics";
import * as XLSX from "xlsx";

const CompanyAnalytics = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const [drillDownVisible, setDrillDownVisible] = useState(false);
    const [drillDownTitle, setDrillDownTitle] = useState("");
    const [drillDownType, setDrillDownType] = useState("");
    const [drillDownRecord, setDrillDownRecord] = useState(null);

    const { data: companyData, isFetching: isLoading } = useGetCompanyAnalyticsQuery({
        page: currentPage,
        limit: pageSize,
        sortField,
        sortOrder,
    });

    const tableData = companyData?.data || [];
    const meta = companyData?.meta || { totalCompanies: 0, page: currentPage, limit: pageSize };

    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;
        const exportData = tableData.map(item => ({
            "Company Name": item.company,
            "Total Events Participated": item.totalEventsParticipated,
            "Total Delegates": item.totalDelegates,
            "Total Sponsors": item.totalSponsors,
            "Meetings Requested": item.meetingStats?.totalRequested || 0,
            "Accepted Meetings": item.meetingStats?.accepted || 0,
            "Pending Meetings": item.meetingStats?.pending || 0,
            "Rejected Meetings": item.meetingStats?.rejected || 0,
            "Acceptance Rate %": item.meetingStats?.acceptanceRate || 0
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Company Analytics");
        XLSX.writeFile(wb, "Company_Analytics_Report.xlsx");
    };

    const openDrillDown = (record, type) => {
        setDrillDownRecord(record);
        setDrillDownType(type);
        if (type === "events") {
            setDrillDownTitle(`Events — ${record.company}`);
        } else if (type === "delegates") {
            setDrillDownTitle(`Delegates — ${record.company}`);
        } else if (type === "sponsors") {
            setDrillDownTitle(`Sponsors — ${record.company}`);
        }
        setDrillDownVisible(true);
    };

    const columns = [
        {
            title: 'Company Name',
            dataIndex: 'company',
            key: 'company',
            sorter: true,
            sortOrder: sortField === 'company' ? sortOrder : null,
        },
        {
            title: 'Total Events',
            dataIndex: 'totalEventsParticipated',
            key: 'totalEventsParticipated',
            sorter: true,
            sortOrder: sortField === 'totalEventsParticipated' ? sortOrder : null,
            render: (val, record) => (
                <span
                    className="text-blue-600 font-semibold cursor-pointer hover:underline"
                    onClick={(e) => { e.stopPropagation(); openDrillDown(record, "events"); }}
                >
                    {val}
                </span>
            ),
        },
        {
            title: 'Total Delegates',
            dataIndex: 'totalDelegates',
            key: 'totalDelegates',
            render: (val, record) => (
                <span
                    className="text-green-600 font-semibold cursor-pointer hover:underline"
                    onClick={(e) => { e.stopPropagation(); openDrillDown(record, "delegates"); }}
                >
                    {val}
                </span>
            ),
        },
        {
            title: 'Total Sponsors',
            dataIndex: 'totalSponsors',
            key: 'totalSponsors',
            render: (val, record) => (
                <span
                    className="text-yellow-600 font-semibold cursor-pointer hover:underline"
                    onClick={(e) => { e.stopPropagation(); openDrillDown(record, "sponsors"); }}
                >
                    {val}
                </span>
            ),
        },
        {
            title: 'Meetings Requested',
            dataIndex: ['meetingStats', 'totalRequested'],
            key: 'totalRequested',
            sorter: true,
            sortOrder: sortField === 'totalRequested' ? sortOrder : null,
        },
        {
            title: 'Acceptance Rate',
            dataIndex: ['meetingStats', 'acceptanceRate'],
            key: 'acceptanceRate',
            sorter: true,
            sortOrder: sortField === 'acceptanceRate' ? sortOrder : null,
            render: val => `${val}%`,
        },
    ];

    const topCompaniesByEvents = useMemo(
        () => ([...tableData].sort((a, b) => b.totalEventsParticipated - a.totalEventsParticipated)[0]),
        [tableData]
    );
    const topCompaniesByMeetings = useMemo(
        () => ([...tableData].sort((a, b) => (b.meetingStats?.totalRequested || 0) - (a.meetingStats?.totalRequested || 0))[0]),
        [tableData]
    );

    const renderDrillDownContent = () => {
        if (!drillDownRecord) return null;

        if (drillDownType === "events") {
            const events = drillDownRecord.events || [];
            if (events.length === 0) return <p className="text-gray-500">No events found.</p>;
            return (
                <div className="space-y-3">
                    {events.map((ev, idx) => (
                        <div key={idx} className="p-4 rounded-lg border border-gray-100 bg-gray-50">
                            <p className="font-semibold text-gray-800 text-base">{ev.name}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                                {ev.location && <span>Location: {ev.location}</span>}
                                {ev.start_date && (
                                    <span>Start: {new Date(ev.start_date).toLocaleDateString()}</span>
                                )}
                                {ev.end_date && (
                                    <span>End: {new Date(ev.end_date).toLocaleDateString()}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (drillDownType === "delegates") {
            const delegates = drillDownRecord.delegates || [];
            if (delegates.length === 0) return <p className="text-gray-500">No delegates found.</p>;
            return (
                <List
                    dataSource={delegates}
                    renderItem={(item) => (
                        <List.Item>
                            <div className="flex justify-between w-full items-center">
                                <span className="font-medium text-gray-700">
                                    {item.first_name} {item.last_name}
                                </span>
                                {item.job_title && (
                                    <Tag color="blue">{item.job_title}</Tag>
                                )}
                            </div>
                        </List.Item>
                    )}
                />
            );
        }

        if (drillDownType === "sponsors") {
            const sponsors = drillDownRecord.sponsors || [];
            if (sponsors.length === 0) return <p className="text-gray-500">No sponsors found.</p>;
            return (
                <List
                    dataSource={sponsors}
                    renderItem={(item) => (
                        <List.Item>
                            <span className="font-medium text-gray-700">
                                {item.first_name} {item.last_name}
                            </span>
                        </List.Item>
                    )}
                />
            );
        }

        return null;
    };

    return (
        <div className="animate-fade-in">
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] gap-4">
                <p className="text-gray-500 m-0 text-sm sm:text-base font-medium">Track company participation across all events and their overall networking performance.</p>
                <Button
                    type="primary"
                    disabled={!tableData || tableData.length === 0}
                    onClick={handleExport}
                    className="bg-primary hover:bg-yellow-600 border-none shadow-md hover:shadow-lg transition-all"
                >
                    Export Data
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-20"><Spin size="large" /></div>
            ) : (
                <>
                    {tableData.length > 0 && (
                        <Row gutter={24} className="mb-8">
                            <Col xs={24} sm={24} md={8}>
                                <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-blue-50 relative overflow-hidden group h-full">
                                    <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 bg-blue-50 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-500"></div>
                                    <Statistic title={<span className="text-gray-500 font-medium">Total Distinct Companies</span>} value={meta.totalCompanies || tableData.length} valueStyle={{ color: '#1890ff', fontSize: '2rem', fontWeight: 'bold' }} />
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={8} className="max-md:mt-4">
                                <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-green-50 relative overflow-hidden group h-full">
                                    <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 bg-green-50 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-500"></div>
                                    <Statistic
                                        title={<span className="text-gray-500 font-medium">Most Active Company (Events)</span>}
                                        value={topCompaniesByEvents?.company || 'N/A'}
                                        suffix={topCompaniesByEvents ? <span className="text-sm font-normal text-gray-400 ml-2">({topCompaniesByEvents.totalEventsParticipated} events)</span> : ''}
                                        valueStyle={{ color: '#52c41a', fontSize: '1.5rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={8} className="max-md:mt-4">
                                <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-yellow-50 relative overflow-hidden group h-full">
                                    <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 bg-yellow-50 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-500"></div>
                                    <Statistic
                                        title={<span className="text-gray-500 font-medium">Most Active Networker</span>}
                                        value={topCompaniesByMeetings?.company || 'N/A'}
                                        suffix={topCompaniesByMeetings ? <span className="text-sm font-normal text-gray-400 ml-2">({topCompaniesByMeetings.meetingStats?.totalRequested} reqs)</span> : ''}
                                        valueStyle={{ color: '#E2B024', fontSize: '1.5rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}

                    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden mt-4">
                        <Table
                            dataSource={tableData}
                            columns={columns}
                            rowKey="company"
                            pagination={{
                                current: currentPage,
                                pageSize,
                                total: meta.totalCompanies || 0,
                                className: 'px-4 mb-4',
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                            }}
                            onChange={(pagination, _filters, sorter) => {
                                if (pagination.current !== currentPage || pagination.pageSize !== pageSize) {
                                    setCurrentPage(pagination.current);
                                    setPageSize(pagination.pageSize);
                                }

                                if (!sorter || Array.isArray(sorter)) {
                                    setSortField(null);
                                    setSortOrder(null);
                                    return;
                                }

                                const newSortKey = sorter.columnKey || null;
                                const newSortOrder = sorter.order || null;

                                if (newSortKey !== sortField || newSortOrder !== sortOrder) {
                                    setSortField(newSortKey);
                                    setSortOrder(newSortOrder);
                                    setCurrentPage(1);
                                }
                            }}
                            rowClassName="hover:bg-gray-50/50 transition-colors"
                        />
                    </div>
                </>
            )}

            <Modal
                open={drillDownVisible}
                title={drillDownTitle}
                footer={null}
                onCancel={() => { setDrillDownVisible(false); setDrillDownRecord(null); }}
                width={640}
                bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
            >
                {renderDrillDownContent()}
            </Modal>
        </div>
    );
};

export default CompanyAnalytics;
